import { call, select } from "redux-saga/effects";

import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { AnyEntity } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";

import RecordsCadenceEffectivenessCardData from "@app/scene/dashboard/store/cards/recordsCadenceEffectiveness/RecordsCadenceEffectivenessCardData";
import { callApi } from "@app/store/api/callApi";
import { fetchMethodByEntityType } from "@app/store/dashboard/common";
import computeOverdueBands from "@app/store/dashboard/recordsCadenceEffectiveness/computeOverdueBands";
import { getOrganizationId } from "@app/store/iam";
import RecordsCadenceEffectivenessBand from "@app/types/dashboard/cards/recordsCadenceEffectiveness/RecordsCadenceEffectivenessBand";
import RecordsCadenceEffectivenessCardConfiguration from "@app/types/dashboard/cards/recordsCadenceEffectiveness/RecordsCadenceEffectivenessCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";

/** One bucket of the `cadenceDaysOut` range aggregation, keyed by the band's lower bound. */
interface OverdueBandAggregationItem {
  doc_count: number;
  key: string;
}

/**
 * Base `$filters` shared by the total-records count and every band count: records with a
 * non-null effective cadence (`cadenceDaysOut` is set), scoped to the assignee/team and the
 * configured source. We gate on the effective `cadenceDaysOut` — the same field the bars
 * bucket by — rather than the record-level `cadenceInterval`, so overdue records whose cadence
 * is inherited from a group/territory are still counted and the tooltip matches the drill-down
 * list. The chosen entity-type endpoint (`fetchCompanies`/`fetchPeople`/`fetchDeals`) already
 * excludes leads, which satisfies the "non-lead" half of the total.
 */
export const buildRecordsCadenceEffectivenessFilters = (
  configuration: RecordsCadenceEffectivenessCardConfiguration,
  scope: Scope | undefined
) => {
  const { criteria, extraFilters } = configuration;
  const isTeamScope = !!scope?.teamId && !scope?.userId;

  return {
    ...extraFilters,
    cadenceDaysOut: { [PlatformFilterOperator.NOT_EQUAL]: null },
    source: criteria.sources.length
      ? { [PlatformFilterOperator.CONTAINS]: criteria.sources }
      : undefined,
    teamId: isTeamScope ? { [PlatformFilterOperator.CONTAINS]: [scope!.teamId!] } : undefined,
    userId: scope?.userId ? { [PlatformFilterOperator.CONTAINS]: [scope.userId] } : undefined,
  };
};

export function* fetchRecordsCadenceEffectivenessCardDataHelper(params: {
  callback: (data: RecordsCadenceEffectivenessCardData) => void;
  configuration: RecordsCadenceEffectivenessCardConfiguration;
  scope?: Scope;
}) {
  const { callback, configuration, scope } = params;

  const orgId: Organization["id"] = yield select(getOrganizationId);
  const fetchMethod = fetchMethodByEntityType[configuration.criteria.entityType];

  const filters = buildRecordsCadenceEffectivenessFilters(configuration, scope);

  // One grouped request: `total` is the total record count (all cadence-configured records in
  // scope) and the server-side `range` aggregation on `cadenceDaysOut` returns a per-band
  // count. Bands are exclusive, inclusive-lower (30→[30,60), 60→[60,90), 90→[90,∞)); the
  // named range keys match the band enum values so counts map deterministically.
  const payload = {
    $aggs: {
      count: ["id"],
      primaryGroup: {
        field: "cadenceDaysOut",
        ranges: [
          {
            from: RecordsCadenceEffectivenessBand.THIRTY,
            key: `${RecordsCadenceEffectivenessBand.THIRTY}`,
            to: RecordsCadenceEffectivenessBand.SIXTY,
          },
          {
            from: RecordsCadenceEffectivenessBand.SIXTY,
            key: `${RecordsCadenceEffectivenessBand.SIXTY}`,
            to: RecordsCadenceEffectivenessBand.NINETY,
          },
          {
            from: RecordsCadenceEffectivenessBand.NINETY,
            key: `${RecordsCadenceEffectivenessBand.NINETY}`,
          },
        ],
      },
    },
    $filters: filters,
    $limit: 0,
  };

  const response: AggregatedListResponse<AnyEntity, OverdueBandAggregationItem[]> = yield callApi(
    fetchMethod,
    orgId,
    payload
  );

  // A `range` aggregation always returns a bucket per requested range (doc_count 0 when
  // empty), so every band key is present — no per-band fallback needed.
  const countByBand = response.aggregations.reduce<Record<string, number>>((acc, item) => {
    acc[item.key] = item.doc_count;
    return acc;
  }, {});

  const result: RecordsCadenceEffectivenessCardData = {
    overdue: computeOverdueBands({
      band30: countByBand[RecordsCadenceEffectivenessBand.THIRTY],
      band60: countByBand[RecordsCadenceEffectivenessBand.SIXTY],
      band90: countByBand[RecordsCadenceEffectivenessBand.NINETY],
      totalRecords: response.total,
    }),
  };

  yield call(callback, result);
}
